import React, { useState, useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LandingPage from "./pages/LandingPage";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy-loaded routes — only downloaded when the user navigates to them
const Dashboard = lazy(() => import("./pages/Dashboard"));
const TeamSelection = lazy(() => import("./pages/TeamSelection"));
const Leaderboards = lazy(() => import("./pages/Leaderboards"));
const PlayerDetail = lazy(() => import("./pages/PlayerDetail"));
const Players = lazy(() => import("./pages/Players"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Settings = lazy(() => import("./pages/Settings"));
const SurveyPopup = lazy(() => import("./components/SurveyPopup"));
const OffSeasonPage = lazy(() => import("./components/OffSeasonPage"));
const CGApp = lazy(() => import("./pages/cg/CGApp"));
const CGTerms = lazy(() => import("./pages/cg/CGTerms"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth context
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    // Fetch site settings first
    fetchSiteSettings();
    
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/site-settings`);
      setSiteSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch site settings", error);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user", error);
      localStorage.removeItem("token");
      delete axios.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  // Admin login handler for off-season page
  const handleAdminLogin = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token, user: userData } = response.data;
      
      // Check if user is admin
      if (!userData.is_admin) {
        throw new Error("Admin access required");
      }
      
      login(token, userData);
    } catch (error) {
      throw new Error(error.response?.data?.detail || "Login failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#d7efec]">
        <div className="text-xl font-semibold text-teal-700">Loading...</div>
      </div>
    );
  }

  // Show off-season page if enabled and user is not admin (bypass for password reset pages)
  const isPasswordPage = window.location.pathname.startsWith('/forgot-password') || window.location.pathname.startsWith('/reset-password');
  if (siteSettings?.off_season?.enabled && !user?.is_admin && !isPasswordPage) {
    return (
      <OffSeasonPage 
        config={siteSettings.off_season} 
        onAdminLogin={handleAdminLogin}
      />
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, fetchUser, siteSettings }}>
      <div className="App">
        <Toaster position="top-right" />
        
        {/* Survey Popup - only show when user is logged in */}
        {user && siteSettings?.survey_popup?.enabled && (
          <SurveyPopup config={siteSettings.survey_popup} />
        )}
        
        <BrowserRouter>
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-[#d7efec]">
              <div className="text-xl font-semibold text-teal-700">Loading...</div>
            </div>
          }>
            <Routes>
              {/* CG Admin - archived, admin-only access */}
              <Route path="/cg-admin" element={user?.is_admin ? <CGApp adminOnly /> : <Navigate to="/" />} />

              <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/" />} />
              <Route path="/team" element={user ? <TeamSelection /> : <Navigate to="/" />} />
              <Route path="/players" element={user ? <Players /> : <Navigate to="/" />} />
              <Route path="/leaderboards" element={user ? <Leaderboards /> : <Navigate to="/" />} />
              <Route path="/player/:playerId" element={user ? <PlayerDetail /> : <Navigate to="/" />} />
              <Route path="/settings" element={user ? <Settings /> : <Navigate to="/" />} />
              <Route path="/admin" element={user?.is_admin ? <AdminPanel /> : <Navigate to="/dashboard" />} />
              <Route path="/admin/users" element={user?.is_admin ? <ErrorBoundary><AdminDashboard /></ErrorBoundary> : <Navigate to="/dashboard" />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </AuthContext.Provider>
  );
}

export default App;
export { API };